import React, { useCallback, useRef } from 'react';
import Map, { Marker, Popup, MapRef } from 'react-map-gl/mapbox';
import { UserCurrentTaskResp } from 'domain/payload/response/user/UserViewResp';
import { MAPBOX_TOKEN } from 'config/Mapbox';

interface TaskMapViewProps {
  tasks: UserCurrentTaskResp[];
  selectedTask?: UserCurrentTaskResp | null;
  onSelectTask: (task: UserCurrentTaskResp | null) => void;
  contentHeight: string;
}

const TaskMapView: React.FC<TaskMapViewProps> = ({
  tasks,
  selectedTask,
  onSelectTask,
  contentHeight,
}) => {
  const mapRef = useRef<MapRef | null>(null)
  const hasTasks = tasks && tasks.length > 0

  const tasksWithLocation = (tasks || []).filter(
    task => task.latitude && task.longitude
  )

  const handleMapLoad = useCallback(() => {
    if (!mapRef.current || tasksWithLocation.length === 0) return

    const lngs = tasksWithLocation.map(t => t.longitude!)
    const lats = tasksWithLocation.map(t => t.latitude!)

    mapRef.current.fitBounds(
      [
        [Math.min(...lngs), Math.min(...lats)],
        [Math.max(...lngs), Math.max(...lats)],
      ],
      { padding: 60, duration: 500 }
    )
  }, [tasksWithLocation])

  if (!hasTasks) {
    return (
      <div className="text-center py-5" style={{ height: contentHeight }}>
        <i className="fa-solid fa-map text-muted fa-3x mb-2"></i>
        <p className="text-muted">No tasks to display on map</p>
      </div>
    )
  }

  if (tasksWithLocation.length === 0) {
    return (
      <div className="text-center py-5" style={{ height: contentHeight }}>
        <i className="fa-solid fa-map-marker-slash text-muted fa-3x mb-2"></i>
        <p className="text-muted">No tasks with location data available</p>
      </div>
    )
  }

  return (
    <div className="position-relative h-100" style={{ height: contentHeight }}>
      <Map
        ref={mapRef}
        mapboxAccessToken={MAPBOX_TOKEN}
        initialViewState={{
          longitude: tasksWithLocation[0].longitude!,
          latitude: tasksWithLocation[0].latitude!,
          zoom: 12,
        }}
        style={{ width: '100%', height: '100%' }}
        mapStyle="mapbox://styles/mapbox/streets-v12"
        onLoad={handleMapLoad}
      >
        {tasksWithLocation.map((task, index) => (
          <Marker
            key={task.id || index}
            longitude={task.longitude!}
            latitude={task.latitude!}
            anchor="bottom"
            onClick={(e) => {
              e.originalEvent.stopPropagation()
              onSelectTask(task)

              mapRef.current?.flyTo({
              center: [task.longitude, task.latitude],
              duration: 500,
              offset: [0, 100],
            })
            }}
          >
            <img
              src="/assets/markers/icon-marker.svg"
              style={{ width: 25, height: 25, cursor: 'pointer' }}
              title={task.name || task.taskName || `Task ${index + 1}`}
              alt=""
            />
          </Marker>
        ))}

        {selectedTask?.latitude && selectedTask?.longitude && (
          <Popup
            longitude={selectedTask.longitude}
            latitude={selectedTask.latitude}
            anchor="bottom"
            offset={[0, -30]}
            onClose={() => onSelectTask(null)}
            closeOnClick={false}
            maxWidth="220px"
          >
            <div style={{ maxWidth: 200 }}>
              <h6 className="fw-bold mb-1">
                {selectedTask.name || selectedTask.taskName || 'Unnamed Task'}
              </h6>
              <div className="mb-1">
                <small className="badge bg-light text-dark border">
                  {selectedTask.taskType?.value || 'N/A'}
                </small>
              </div>
              <p className="mb-1 small">
                {selectedTask.placeName || selectedTask.placeAddress || 'No address'}
              </p>
              <div className="mb-1 small">
                <span className={`badge ${selectedTask.status?.value === 'Completed' ? 'bg-success' : 'bg-warning'}`}>
                  {selectedTask.status?.value || 'In Progress'}
                </span>
              </div>
              <div className="small text-muted">
                <i className="fa-solid fa-location-dot me-1"></i>
                {selectedTask.latitude?.toFixed(6)}, {selectedTask.longitude?.toFixed(6)}
              </div>
            </div>
          </Popup>
        )}
      </Map>

      <div className="position-absolute top-0 m-2" style={{ zIndex: 1 }}>
        <div className="bg-white rounded shadow-sm p-2">
          <small className="text-muted">
            <i className="fa-solid fa-map-marker-alt me-1"></i>
            {tasksWithLocation.length} locations shown
          </small>
        </div>
      </div>
    </div>
  )

};

export default TaskMapView;